import { ILegoElem } from '@src/molecules/Lego';
import { IOptionFilter } from '@src/models/api';
import { useLegoFormContext } from '@src/molecules/Lego/_FormProvider/useLegoFormContext';
import { useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';

/**
 * В Lego формах могут встречаться зависимые элементы.
 * Например Select с клиеинтами, от которого зависит Select с продуктами выбранного клиента,
 * от которого зависит Select с кабинетами выбранного продукта в рамках выбранного клиента.
 * Lego само должно следить за тем, какие данные надо загрузить с бекенда и в какой момент,
 * а для того, чтобы мы работали универсально мы всегда будем генерировать зависимости для `loadOptions`
 * в рамках указанного элемента. Далее зависимости поступят на вход `useLoadOptions` и будут загружены с сервера.
 */
export const useLegoElemDependsOptionFilters = (elem: ILegoElem, onChangeCb: () => void): IOptionFilter[] => {
  const { formHook } = useLegoFormContext();

  const dependsKeys = useMemo(() => {
    return (elem.depends || []).map((field) =>
      typeof field === 'string' ? { field: field, useInFilter: true } : field,
    );
  }, [elem.depends]);

  const [currDependsEntries, setCurrDependsEntries] = useState<IOptionFilter[]>([]);
  const dependsValues = formHook.watch(dependsKeys.map((k) => k.field));

  useEffect(() => {
    const dependsEntries: IOptionFilter[] = [];
    let idx = 0;
    for (const key of dependsKeys) {
      dependsEntries.push({
        field: key.field,
        value: dependsValues[idx++],
        useInFilter: key.useInFilter,
      });
    }

    if (!isEqual(currDependsEntries, dependsEntries)) {
      onChangeCb();
      setCurrDependsEntries(dependsEntries);
    }
  }, [onChangeCb, currDependsEntries, dependsKeys, dependsValues]);

  return currDependsEntries;
};
